import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import { getLatestLecture, enrollLecture, getMyLectureList, getProgress, getAttendances, getStudyTime, getMyPosts, getCertificates } from '../../services/mypageApi';
import { Certificate } from '../../types/certificate';

const MyPageHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  // 최신 강의 상태 관리
  const [latestLecture, setLatestLecture] = useState<{
    lecture_info: {
      id: number;
      title: string;
      caption: string;
      thumbnail_url: string;
      tags: string[];
    };
    lecturer_info: {
      id: number;
      name: string;
      introduction: string;
      image_url: string;
      tags: string[];
    };
    status: string;
  } | null>(null);
  const [isLoadingLatest, setIsLoadingLatest] = useState(true);
  const [latestLectureError, setLatestLectureError] = useState<string | null>(null);

  // 드롭다운 및 진도율 상태 관리
  const [selectedLecture, setSelectedLecture] = useState<{id: number | null, title: string}>({id: null, title: '전체 강의'});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [lectureList, setLectureList] = useState<Array<{id: number, title: string}>>([]);
  const [progressData, setProgressData] = useState({
    completion_rate: 0,
    total_time_watched: '0h0m0s',
    total_time_expected: '0h0m0s'
  });
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);

  // 출석 상태 관리
  const [attendanceData, setAttendanceData] = useState<Array<{date: string, attended: boolean}>>([]);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);

  // 학습시간 관리
  const [selectedPeriod, setSelectedPeriod] = useState<{days: number, label: string}>({days: 7, label: '최근 7일'});
  const [isStudyTimeDropdownOpen, setIsStudyTimeDropdownOpen] = useState(false);
  const [studyTimeData, setStudyTimeData] = useState<Array<{date: string, total_study_time_in_seconds: number}>>([]);
  const [isLoadingStudyTime, setIsLoadingStudyTime] = useState(false);

  // 학습시간 기간 옵션
  const studyTimePeriods = [
    { days: 7, label: '최근 7일' },
    { days: 14, label: '최근 14일' },
    { days: 21, label: '최근 21일' },
    { days: 30, label: '최근 30일' }
  ];

  // 호버 상태 관리
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{x: number, y: number} | null>(null);

  // 홈페이지용 강의 목록 관리 (3개만 표시)
  const [homeLectureList, setHomeLectureList] = useState<Array<{
    lecture_info: {
      id: number;
      title: string;
      caption: string;
      thumbnail_url: string;
      tags: string[];
    };
    lecturer_info: {
      id: number;
      name: string;
      introduction: string;
      image_url: string;
      tags: string[];
    };
    status: string;
  }>>([]);
  const [isLoadingHomeLectures, setIsLoadingHomeLectures] = useState(false);

  // 나의 게시글 관리 (2개만 표시)
  const [myPosts, setMyPosts] = useState<Array<{
    post_id: number;
    title: string;
    content: string;
    post_category: string;
    reply_cnt: number;
    view_cnt: number;
    writer_name: string;
    created_at: string;
    lecture_name: string;
    chapter: number;
    study_name: string;
    pinned: boolean;
  }>>([]);
  const [isLoadingMyPosts, setIsLoadingMyPosts] = useState(false);

  // 수료증 관리 (최대 4개만 표시)
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [isLoadingCertificates, setIsLoadingCertificates] = useState(false);

  // 날짜 범위 계산 함수 (오늘 기준)
  const getDateRangeString = (days: number) => {
    const endDate = new Date(); // 오늘 날짜
    
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - days + 1);
    
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };
    
    const result = `${formatDate(startDate)}~${formatDate(endDate)}`;
    console.log(`날짜 범위 계산 (오늘 기준):`, {
      오늘: formatDate(endDate),
      시작일: formatDate(startDate),
      결과: result
    });
    
    return result;
  };

  // 최신 강의 데이터 로드
  useEffect(() => {
    const fetchLatestLecture = async () => {
      try {
        setIsLoadingLatest(true);
        setLatestLectureError(null);
        const data = await getLatestLecture();
        setLatestLecture(data);
        console.log('최신 강의 데이터:', data);
      } catch (error) {
        console.error('최신 강의 로드 실패:', error);
        setLatestLectureError(error instanceof Error ? error.message : '최신 강의를 불러오는데 실패했습니다.');
      } finally {
        setIsLoadingLatest(false);
      }
    };

    fetchLatestLecture();
  }, []);

  // 강의 목록 로드
  useEffect(() => {
    const fetchLectureList = async () => {
      try {
        console.log('강의 목록 API 호출 시작...');
        const data = await getMyLectureList(undefined, 0, 100);
        const lectures = data.content.map(item => ({
          id: item.lecture_info.id,
          title: item.lecture_info?.title
        }));
        setLectureList(lectures);
        console.log('강의 목록 로드 완료:', lectures);
      } catch (error) {
        console.error('강의 목록 로드 실패:', error);
        // API 실패 시 더미 데이터 폴백
        const dummyLectures = [
          { id: 1, title: '의학 기초 해부학' },
          { id: 2, title: '생리학 완전정복' },
          { id: 3, title: '병리학 심화과정' },
          { id: 4, title: '약리학 실전 문제' },
          { id: 5, title: '내과 임상 실습' }
        ];
        setLectureList(dummyLectures);
        console.log('더미 강의 목록 적용 (API 실패):', dummyLectures);
      }
    };

    fetchLectureList();
  }, []);

  // 홈페이지용 강의 목록 로드 (3개만)
  useEffect(() => {
    const fetchHomeLectures = async () => {
      try {
        setIsLoadingHomeLectures(true);
        console.log('홈페이지용 강의 목록 API 호출 시작...');
        const data = await getMyLectureList(undefined, 0, 3); // 3개만 로드
        setHomeLectureList(data.content);
        console.log('홈페이지용 강의 목록 로드 완료:', data.content);
      } catch (error) {
        console.error('홈페이지용 강의 목록 로드 실패:', error);
        // API 실패 시 빈 배열
        setHomeLectureList([]);
      } finally {
        setIsLoadingHomeLectures(false);
      }
    };

    fetchHomeLectures();
  }, []);

  // 나의 게시글 로드 (2개만)
  useEffect(() => {
    const fetchMyPosts = async () => {
      try {
        setIsLoadingMyPosts(true);
        console.log('나의 게시글 API 호출 시작...');
        const data = await getMyPosts(0, 2); // 2개만 로드
        setMyPosts(data.content);
        console.log('나의 게시글 로드 완료:', data.content);
      } catch (error) {
        console.error('나의 게시글 로드 실패:', error);
        // API 실패 시 더미 데이터 사용
        const dummyPosts = [
          {
            post_id: 1,
            title: "의학 기초 해부학 강의에서 질문이 있습니다",
            content: "심장의 구조에 대해 자세히 알고 싶습니다.",
            post_category: "강의 질문",
            reply_cnt: 3,
            view_cnt: 45,
            writer_name: "학습자",
            created_at: "2025-07-25T10:30:00Z",
            lecture_name: "의학 기초 해부학",
            chapter: 2,
            study_name: "기초의학",
            pinned: false
          },
          {
            post_id: 2,
            title: "생리학 완전정복 수강 후기입니다",
            content: "정말 유익한 강의였습니다. 감사합니다.",
            post_category: "수강 후기",
            reply_cnt: 1,
            view_cnt: 23,
            writer_name: "학습자",
            created_at: "2025-07-24T14:15:00Z",
            lecture_name: "생리학 완전정복",
            chapter: 0,
            study_name: "기초의학",
            pinned: true
          }
        ];
        setMyPosts(dummyPosts);
        console.log('더미 게시글 데이터 적용:', dummyPosts);
      } finally {
        setIsLoadingMyPosts(false);
      }
    };

    fetchMyPosts();
  }, []);

  // 수료증 데이터 로드
  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setIsLoadingCertificates(true);
        console.log('수료증 API 호출 시작...');
        const data = await getCertificates(0, 4); // 4개만 로드
        setCertificates(data.content);
        console.log('수료증 로드 완료:', data.content);
      } catch (error) {
        console.error('수료증 로드 실패:', error);
        // API 실패 시 빈 배열
        setCertificates([]);
      } finally {
        setIsLoadingCertificates(false);
      }
    };

    fetchCertificates();
  }, []);

  // 진도율 로드
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setIsLoadingProgress(true);
        console.log('진도율 API 호출:', selectedLecture);
        
        const data = await getProgress(selectedLecture.id || undefined);
        setProgressData({
          completion_rate: data.completion_rate * 100, // 백분율로 변환
          total_time_watched: data.total_time_watched,
          total_time_expected: data.total_time_expected
        });
        console.log('진도율 로드 완료:', data);
      } catch (error) {
        console.error('진도율 로드 실패:', error);
        // API 실패 시 더미 데이터 또는 기본값
        if (selectedLecture.id === null) {
          // 전체 강의 선택 시 더미 데이터
          setProgressData({
            completion_rate: 35,
            total_time_watched: '12h30m45s',
            total_time_expected: '35h20m0s'
          });
          console.log('전체 강의 더미 데이터 적용');
        } else {
          // 개별 강의 선택 시 기본값
          setProgressData({
            completion_rate: 0,
            total_time_watched: '0h0m0s',
            total_time_expected: '0h0m0s'
          });
          console.log('개별 강의 기본값 적용');
        }
      } finally {
        setIsLoadingProgress(false);
      }
    };

    fetchProgress();
  }, [selectedLecture.id]);

  // 출석 데이터 로드
  useEffect(() => {
    const fetchAttendances = async () => {
      try {
        setIsLoadingAttendance(true);
        console.log('출석 데이터 로드:', currentMonth);
        
        const data = await getAttendances(currentMonth.year, currentMonth.month);
        setAttendanceData(data.attendance_monthly_list);
        console.log('출석 데이터 로드 완료:', data);
      } catch (error) {
        console.error('출석 데이터 로드 실패:', error);
        // 에러 시 빈 배열
        setAttendanceData([]);
      } finally {
        setIsLoadingAttendance(false);
      }
    };

    fetchAttendances();
  }, [currentMonth.year, currentMonth.month]);

  // 학습시간 데이터 로드
  useEffect(() => {
    const fetchStudyTime = async () => {
      try {
        setIsLoadingStudyTime(true);
        
        // 날짜 계산 (오늘 기준)
        const endDate = new Date(); // 오늘 날짜
        
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - selectedPeriod.days + 1);
        
        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];
        
        console.log('학습시간 데이터 로드:', { selectedPeriod, startDateStr, endDateStr });
        console.log('학습시간 API 호출 시작');
        
        const data = await getStudyTime(startDateStr, endDateStr);
        console.log('학습시간 API 응답:', data);
        setStudyTimeData(data);
        console.log('학습시간 데이터 로드 완료:', data);
      } catch (error) {
        console.error('학습시간 데이터 로드 실패:', error);
        // 에러 시 빈 배열
        setStudyTimeData([]);
      } finally {
        setIsLoadingStudyTime(false);
      }
    };

    fetchStudyTime();
  }, [selectedPeriod.days]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
      if (isStudyTimeDropdownOpen) {
        setIsStudyTimeDropdownOpen(false);
      }
    };

    if (isDropdownOpen || isStudyTimeDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen, isStudyTimeDropdownOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // 강의 클릭 핸들러
  const handleLectureClick = (lectureId: number) => {
    navigate(`/class/lecture/${lectureId}`);
  };

  // 드롭다운 토글
  const toggleDropdown = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('드롭다운 버튼 클릭!');
    console.log('현재 isDropdownOpen:', isDropdownOpen);
    console.log('강의 목록 개수:', lectureList.length);
    setIsDropdownOpen(!isDropdownOpen);
    console.log('드롭다운 토글 완료:', !isDropdownOpen);
  };

  // 강의 선택 핸들러
  const handleLectureSelect = (lectureId: number | null, title: string) => {
    setSelectedLecture({id: lectureId, title});
    setIsDropdownOpen(false);
  };

  // 전체 강의 선택 핸들러
  const handleSelectAllLectures = () => {
    handleLectureSelect(null, '전체 강의');
  };

  // 학습시간 드롭다운 토글 핸들러
  const handleStudyTimeDropdownToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsStudyTimeDropdownOpen(!isStudyTimeDropdownOpen);
  };

  // 학습시간 기간 선택 핸들러
  const handlePeriodSelect = (days: number, label: string) => {
    setSelectedPeriod({days, label});
    setIsStudyTimeDropdownOpen(false);
  };

  // 시간 형식 변환 함수 (0h0m0s -> 0:00:00)
  const formatTime = (timeString: string): string => {
    if (!timeString) return '0:00:00';
    
    // 0h0m0s 형태를 0:00:00 형태로 변환
    const match = timeString.match(/(\d+)h(\d+)m(\d+)s/);
    if (match) {
      const hours = match[1].padStart(1, '0');
      const minutes = match[2].padStart(2, '0');
      const seconds = match[3].padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    }
    
    // 이미 0:00:00 형태라면 그대로 반환
    if (timeString.includes(':')) {
      return timeString;
    }
    
    return '0:00:00';
  };

  // 강의 스트리밍 클릭 핸들러 (수강 신청 후 새창)
  const handleLectureStreamingClick = async (lectureId: number) => {
    try {
      console.log('=== 이어보기 버튼 클릭 ===');
      console.log('강의 ID:', lectureId);

      // 수강 신청 API 호출
      const result = await enrollLecture(lectureId);
      
      // 성공 또는 이미 수강신청된 경우 스트리밍 창 열기
      if (result.code === 200 || result.code === 201 || result.code === 409) {
        console.log('수강 신청 처리 완료. 스트리밍 창을 엽니다.');
        openStreamingWindow(lectureId);
      } else {
        console.log('수강 신청 실패:', result.message);
        const errorMessage = result.message || '알 수 없는 오류가 발생했습니다';
        alert(`수강 신청에 실패했습니다: ${errorMessage}`);
      }
    } catch (error: any) {
      console.error('이어보기 처리 중 오류:', error);
      
      if (error.message === 'AUTH_EXPIRED') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else if (error.message?.startsWith('SERVER_ERROR:')) {
        const errorMessage = error.message.replace('SERVER_ERROR: ', '');
        alert(`서버 오류: ${errorMessage}`);
      } else {
        alert('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
      }
    }
  };

  // 스트리밍 창 열기 함수
  const openStreamingWindow = (lectureId: number) => {
    console.log('=== 스트리밍 창 열기 시작 ===');
    console.log('강의 ID:', lectureId);
    
    // 새 창으로 스트리밍 페이지 띄우기
    const streamingUrl = `/class/streaming/${lectureId}`;
    console.log('스트리밍 URL:', streamingUrl);
    console.log('전체 URL:', window.location.origin + streamingUrl);
    
    const newWindow = window.open(streamingUrl, '_blank', 'width=1440,height=900,scrollbars=yes,resizable=yes');
    
    if (newWindow) {
      console.log('스트리밍 창이 성공적으로 열렸습니다.');
    } else {
      console.error('스트리밍 창을 열 수 없습니다. 팝업이 차단되었을 수 있습니다.');
      alert('새 창을 열 수 없습니다. 팝업 차단을 해제해주세요.');
    }
  };

  // 달력 이동 함수
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 1) {
        return { year: prev.year - 1, month: 12 };
      } else {
        return { ...prev, month: prev.month - 1 };
      }
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 12) {
        return { year: prev.year + 1, month: 1 };
      } else {
        return { ...prev, month: prev.month + 1 };
      }
    });
  };

  // 달력 데이터 (출석 정보) - 이제 사용안함, getAttendanceType에서 처리

  // 현재 날짜 계산
  const now = new Date();

  // 달력 생성 함수
  const generateCalendar = () => {
    const year = currentMonth.year;
    const month = currentMonth.month;
    
    // 해당 월의 첫째 날과 마지막 날
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0(일요일) ~ 6(토요일)
    
    const calendar = [];
    
    // 첫 주의 빈 칸들
    for (let i = 0; i < startDayOfWeek; i++) {
      calendar.push(null);
    }
    
    // 실제 날짜들
    for (let date = 1; date <= daysInMonth; date++) {
      calendar.push(date);
    }
    
    return calendar;
  };

  const calendarDays = generateCalendar();

  // 출석 타입 확인 (실제 API 데이터 기반)
  const getAttendanceType = (date: number): 'filled' | 'outline' | 'border' | null => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1;
    const todayDate = today.getDate();
    
    // 현재 보고 있는 달력과 실제 오늘 날짜가 같은 년월인지 확인
    const isCurrentMonth = currentMonth.year === todayYear && currentMonth.month === todayMonth;
    
    // API 데이터에서 해당 날짜의 출석 정보 찾기
    const dateString = `${currentMonth.year}-${currentMonth.month.toString().padStart(2, '0')}-${date.toString().padStart(2, '0')}`;
    const attendanceRecord = attendanceData.find(record => record.date === dateString);
    
    console.log(`날짜 ${date}: dateString=${dateString}, attendanceRecord=`, attendanceRecord, `오늘=${isCurrentMonth && date === todayDate}`);
    
    // 오늘 날짜면서 출석한 날 -> border (테두리 있는 채워진 동그라미)
    if (isCurrentMonth && date === todayDate && attendanceRecord?.attended) {
      return 'border';
    }
    
    // 오늘 날짜 (출석 여부와 상관없이) -> outline (안 채워진 동그라미)
    if (isCurrentMonth && date === todayDate) {
      return 'outline';
    }
    
    // 출석한 날 (오늘이 아닌) -> filled (채워진 동그라미)
    if (attendanceRecord?.attended) {
      return 'filled';
    }
    
    // 출석하지 않은 날이나 데이터가 없는 날 -> null (동그라미 없음)
    return null;
  };

  // 출석 통계 계산
  const getAttendanceStats = () => {
    const attendedDays = attendanceData.filter(record => record.attended).length;
    const totalDays = attendanceData.length;
    return { attendedDays, totalDays };
  };

  const attendanceStats = getAttendanceStats();

  return (
    <MyPageLayout hideFooter={true}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>마이페이지</PageTitle>
          </PageHeader>

          {/* 브레드크럼 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>마이페이지</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>홈</BreadcrumbItem>
            </BreadcrumbContainer>
          </NavigationBar>

          {/* 최근 수강한 강의 */}
          {latestLecture?.lecture_info?.id && <Section>
            <SectionHeader>
              <SectionTitle>최근 수강한 강의</SectionTitle>
            </SectionHeader>
            {isLoadingLatest ? (
              <RecentLectureCard>
                <LectureInfo>
                  <LectureThumbnail>
                    <SkeletonThumbnail />
                  </LectureThumbnail>
                  <LectureDetails>
                    <LectureTags>
                      <SkeletonTag />
                    </LectureTags>
                    <LectureContent>
                      <SkeletonTitle />
                      <SkeletonInstructor />
                    </LectureContent>
                  </LectureDetails>
                </LectureInfo>
                <LectureActions>
                  <SkeletonButton />
                  <SkeletonButton />
                </LectureActions>
              </RecentLectureCard>
            ) : latestLectureError ? (
              <ErrorCard>
                <ErrorMessage>최신 강의를 불러오는데 실패했습니다.</ErrorMessage>
                <ErrorSubMessage>{latestLectureError}</ErrorSubMessage>
              </ErrorCard>
            ) : latestLecture ? (
              <RecentLectureCard>
                <LectureInfo>
                  <LectureThumbnail>
                    <img 
                      src={latestLecture.lecture_info?.thumbnail_url || 'https://placehold.co/160x107'} 
                      alt="강의 썸네일"
                      onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/160x107';
                      }}
                    />
                  </LectureThumbnail>
                  <LectureDetails>
                    <LectureTags>
                      {latestLecture.lecture_info?.tags && latestLecture.lecture_info?.tags.length > 0 ? (
                        latestLecture.lecture_info?.tags.map((tag, index) => (
                          <LectureTag key={index}>{tag}</LectureTag>
                        ))
                      ) : (
                        <LectureTag>강의</LectureTag>
                      )}
                    </LectureTags>
                    <LectureContent>
                      <LectureTitle>{latestLecture.lecture_info?.title}</LectureTitle>
                      <LectureInstructor>
                        {latestLecture.lecturer_info?.name || "강사"} · {latestLecture.lecturer_info?.introduction || "강사 소개"}
                      </LectureInstructor>
                    </LectureContent>
                  </LectureDetails>
                </LectureInfo>
                <LectureActions>
                  <ActionButton 
                    primary
                    onClick={() => handleLectureStreamingClick(latestLecture.lecture_info.id)}
                  >
                    <PlayIcon>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M4.5 3.01L13.06 8.98L4.5 14.97V3.01Z" fill="white"/>
                      </svg>
                    </PlayIcon>
                    이어보기
                  </ActionButton>
                  <ActionButton 
                    secondary
                    onClick={() => handleLectureClick(latestLecture.lecture_info.id)}
                  >
                    강의 바로가기
                  </ActionButton>
                </LectureActions>
              </RecentLectureCard>
            ) : (
              <NoLectureCard>
                <NoLectureMessage>아직 수강한 강의가 없습니다.</NoLectureMessage>
                <NoLectureSubMessage>강의를 수강하여 학습을 시작해보세요.</NoLectureSubMessage>
                <NoLectureButton onClick={() => navigate('/class')}>
                  강의 둘러보기
                </NoLectureButton>
              </NoLectureCard>
            )}
          </Section>}

          {/* 학습 통계 */}
          <Section>
            <SectionHeader>
              <SectionTitle>학습 통계</SectionTitle>
            </SectionHeader>
            
            {/* 진도율 */}
            <StatsContainer>
              <ProgressSection>
                <SubSectionHeader>
                  <SubSectionTitle>진도율</SubSectionTitle>
                </SubSectionHeader>
                <ProgressCard>
                  <ProgressTop>
                    <ProgressLabel>{selectedLecture?.title}</ProgressLabel>
                    <DropdownContainer>
                      <DropdownButton onClick={toggleDropdown}>
                        {selectedLecture?.title}
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 14 14" 
                          fill="none"
                          style={{transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}}
                        >
                          <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </DropdownButton>
                      {isDropdownOpen && (
                        <DropdownMenu>
                          <DropdownScrollContainer>
                            <DropdownItem 
                              onClick={handleSelectAllLectures}
                              $isSelected={selectedLecture.id === null}
                            >
                              전체 강의
                            </DropdownItem>
                            {lectureList?.length && lectureList.map((lecture) => (
                              <DropdownItem
                                key={lecture.id}
                                onClick={() => handleLectureSelect(lecture.id, lecture?.title)}
                                $isSelected={selectedLecture.id === lecture.id}
                              >
                                {lecture?.title}
                              </DropdownItem>
                            ))}
                          </DropdownScrollContainer>
                        </DropdownMenu>
                      )}
                    </DropdownContainer>
                  </ProgressTop>
                  <ProgressMain>
                    <ProgressValue>
                      {isLoadingProgress ? '로딩중...' : `${((progressData.completion_rate || 0) / 100).toFixed(2)}%`}
                    </ProgressValue>
                    <ProgressMeta>
                      <ProgressTime>
                        {formatTime(progressData.total_time_watched)} / {formatTime(progressData.total_time_expected)}
                      </ProgressTime>
                      <ProgressDescription>수강한 시간 / 전체 강의 시간</ProgressDescription>
                    </ProgressMeta>
                  </ProgressMain>
                  <ProgressBarContainer>
                    <ProgressBar>
                      <ProgressFill progress={isLoadingProgress ? 0 : (progressData.completion_rate / 100)} />
                    </ProgressBar>
                  </ProgressBarContainer>
                </ProgressCard>
              </ProgressSection>

              <TwoColumnStats>
                {/* 학습 시간 */}
                <StudyTimeSection>
                  <SubSectionHeader>
                    <SubSectionTitle>학습 시간</SubSectionTitle>
                  </SubSectionHeader>
                  <StudyTimeCard>
                    <StudyTimeTop>
                      <StudyTimeLabel>{getDateRangeString(selectedPeriod.days)}</StudyTimeLabel>
                      <DropdownContainer>
                        <DropdownButton onClick={handleStudyTimeDropdownToggle}>
                          {selectedPeriod.label}
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </DropdownButton>
                        {isStudyTimeDropdownOpen && (
                          <DropdownMenu style={{width: '140px'}}>
                            <DropdownScrollContainer>
                              {studyTimePeriods.map((period) => (
                                <DropdownItem
                                  key={period.days}
                                  onClick={() => handlePeriodSelect(period.days, period.label)}
                                  $isSelected={selectedPeriod.days === period.days}
                                >
                                  {period.label}
                                </DropdownItem>
                              ))}
                            </DropdownScrollContainer>
                          </DropdownMenu>
                        )}
                      </DropdownContainer>
                    </StudyTimeTop>
                    <StudyTimeMain>
                      {isLoadingStudyTime ? (
                        <StudyTimeValue>로딩 중...</StudyTimeValue>
                      ) : (
                        <StudyTimeValue>
                          {(() => {
                            const totalSeconds = studyTimeData.reduce((sum, item) => sum + item.total_study_time_in_seconds, 0);
                            const hours = Math.floor(totalSeconds / 3600);
                            const minutes = Math.floor((totalSeconds % 3600) / 60);
                            return `${hours}시간 ${minutes.toString().padStart(2, '0')}분`;
                          })()}
                        </StudyTimeValue>
                      )}
                    </StudyTimeMain>
                    <ChartContainer>
                      <ChartLine />
                      <ChartBars>
                        {studyTimeData.slice(-selectedPeriod.days).map((item, index) => {
                          const maxTime = Math.max(...studyTimeData.map(d => d.total_study_time_in_seconds));
                          const height = maxTime > 0 ? Math.max(20, (item.total_study_time_in_seconds / maxTime) * 194) : 20;
                          console.log(`그래프 데이터 [${index}]:`, {
                            date: item.date,
                            studyTime: item.total_study_time_in_seconds,
                            maxTime,
                            calculatedHeight: height
                          });
                          
                          // 시간 포맷팅 (초 -> 시간분)
                          const formatStudyTime = (seconds: number) => {
                            const hours = Math.floor(seconds / 3600);
                            const minutes = Math.floor((seconds % 3600) / 60);
                            return `${hours}시간 ${minutes.toString().padStart(2, '0')}분`;
                          };
                          
                          // 날짜 포맷팅
                          const formatTooltipDate = (dateString: string) => {
                            const date = new Date(dateString);
                            const month = date.getMonth() + 1;
                            const day = date.getDate();
                            return `${month}월 ${day}일`;
                          };
                          
                          return (
                            <ChartBarContainer 
                              key={`${item.date}-${index}`}
                              onMouseEnter={(e) => {
                                setHoveredBarIndex(index);
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPosition({
                                  x: rect.right + 10,
                                  y: rect.top + rect.height / 2
                                });
                              }}
                              onMouseLeave={() => {
                                setHoveredBarIndex(null);
                                setTooltipPosition(null);
                              }}
                            >
                              <ChartBar 
                                height={height} 
                                $isHovered={hoveredBarIndex === index}
                              />
                              {hoveredBarIndex === index && (
                                <HoverOverlay />
                              )}
                            </ChartBarContainer>
                          );
                        })}
                        
                        {/* 툴팁 */}
                        {hoveredBarIndex !== null && tooltipPosition && (
                          <Tooltip 
                            style={{
                              left: tooltipPosition.x,
                              top: tooltipPosition.y,
                              transform: 'translateY(-50%)',
                              position: 'fixed',
                              zIndex: 1000
                            }}
                          >
                            <TooltipDate>
                              {(() => {
                                const item = studyTimeData.slice(-selectedPeriod.days)[hoveredBarIndex];
                                const date = new Date(item.date);
                                const month = date.getMonth() + 1;
                                const day = date.getDate();
                                return `${month}월 ${day}일`;
                              })()}
                            </TooltipDate>
                            <TooltipTime>
                              {(() => {
                                const item = studyTimeData.slice(-selectedPeriod.days)[hoveredBarIndex];
                                const hours = Math.floor(item.total_study_time_in_seconds / 3600);
                                const minutes = Math.floor((item.total_study_time_in_seconds % 3600) / 60);
                                return `${hours}시간 ${minutes.toString().padStart(2, '0')}분`;
                              })()}
                            </TooltipTime>
                          </Tooltip>
                        )}
                      </ChartBars>
                      <ChartLine />
                    </ChartContainer>
                  </StudyTimeCard>
                </StudyTimeSection>

                {/* 출석 기록 */}
                <AttendanceSection>
                  <SubSectionHeader>
                    <SubSectionTitle>출석 기록</SubSectionTitle>
                  </SubSectionHeader>
                  <AttendanceCard>
                    <AttendanceHeader>
                      <CalendarMonth>{currentMonth.year}년 {currentMonth.month}월</CalendarMonth>
                      <CalendarNavigation>
                        <CalendarArrow onClick={goToPreviousMonth}>
                          <img src="/images/arrow-left.svg" alt="이전 달" width="10" height="21" />
                        </CalendarArrow>
                        <CalendarArrow onClick={goToNextMonth}>
                          <img src="/images/arrow-right.svg" alt="다음 달" width="10" height="21" />
                        </CalendarArrow>
                      </CalendarNavigation>
                    </AttendanceHeader>
                    <CalendarContainer>
                      <CalendarWeekdays>
                        {['일', '월', '화', '수', '목', '금', '토'].map(day => (
                          <CalendarWeekday key={day}>{day}</CalendarWeekday>
                        ))}
                      </CalendarWeekdays>
                      <CalendarGrid>
                        {calendarDays.map((date, index) => (
                          <CalendarDay key={index}>
                            {date && (
                              <CalendarDate attendanceType={getAttendanceType(date)}>
                                {date}
                              </CalendarDate>
                            )}
                          </CalendarDay>
                        ))}
                      </CalendarGrid>
                    </CalendarContainer>
                  </AttendanceCard>
                </AttendanceSection>
              </TwoColumnStats>
            </StatsContainer>
          </Section>

          {/* 나의 강의 */}
          <Section>
            <SectionHeader>
              <SectionTitle>나의 강의</SectionTitle>
              <SectionLink onClick={() => navigate('/mypage/my-classroom')}>
                나의 강의실
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SectionLink>
            </SectionHeader>
            <LectureGrid>
              {isLoadingHomeLectures ? (
                // 로딩 상태
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '200px',
                  color: 'rgba(55, 56, 60, 0.61)',
                  fontSize: '14px',
                  gridColumn: '1 / -1'
                }}>
                  강의 목록 로딩 중...
                </div>
              ) : homeLectureList?.length > 0 ? (
                // 실제 강의 데이터
                homeLectureList.map((lecture, index) => (
                  <LectureCard key={lecture.lecture_info.id} onClick={() => navigate(`/class/lecture/${lecture.lecture_info.id}`)}>
                    <LectureCardThumbnail>
                      <img 
                        src={lecture.lecture_info?.thumbnail_url || "https://placehold.co/282x188"} 
                        alt={`${lecture.lecture_info?.title} 썸네일`} 
                        onError={(e) => {
                          e.currentTarget.src = "https://placehold.co/282x188";
                        }}
                      />
                    </LectureCardThumbnail>
                    <LectureCardContent>
                      {lecture.lecture_info?.tags && lecture.lecture_info?.tags.length > 0 && (
                        <LectureCardTags>
                          {lecture.lecture_info?.tags.map((tag, tagIndex) => (
                            <LectureTag key={tagIndex}>{tag}</LectureTag>
                          ))}
                        </LectureCardTags>
                      )}
                      <LectureCardDetails>
                        <LectureCardTitle>{lecture.lecture_info?.title}</LectureCardTitle>
                        <LectureCardInstructor>
                          {lecture.lecturer_info?.name || "강사"}
                          {lecture.lecturer_info?.introduction && ` · ${lecture.lecturer_info?.introduction}`}
                        </LectureCardInstructor>
                      </LectureCardDetails>
                    </LectureCardContent>
                  </LectureCard>
                ))
              ) : (
                // 강의가 없는 경우
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '200px',
                  color: 'rgba(55, 56, 60, 0.61)',
                  fontSize: '14px',
                  gridColumn: '1 / -1'
                }}>
                  <div>아직 수강 중인 강의가 없습니다.</div>
                  <div style={{marginTop: '8px', fontSize: '12px'}}>
                    새로운 강의를 찾아보세요!
                  </div>
                </div>
              )}
            </LectureGrid>
          </Section>

          {/* 수료증 */}
          <Section>
            <SectionHeader>
              <SectionTitle>수료증</SectionTitle>
              <SectionLink onClick={() => navigate('/mypage/certificate')}>
                수료증
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SectionLink>
            </SectionHeader>
            <CertificateGrid>
              {isLoadingCertificates ? (
                // 로딩 상태
                Array.from({ length: 4 }, (_, index) => (
                  <CertificateCard key={index}>
                    <CertificateThumbnail>
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#999'
                      }}>
                        로딩 중...
                      </div>
                    </CertificateThumbnail>
                    <CertificateTitle>로딩 중...</CertificateTitle>
                  </CertificateCard>
                ))
              ) : certificates.length > 0 ? (
                // 실제 수료증 데이터
                certificates.slice(0, 4).map((certificate) => (
                  <CertificateCard 
                    key={certificate.person_certification_id}
                    onClick={() => navigate('/mypage/certificate')}
                  >
                    <CertificateThumbnail>
                      <img 
                        src={certificate.template_image_url || 'https://placehold.co/205x205'} 
                        alt="수료증"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://placehold.co/205x205';
                        }}
                      />
                    </CertificateThumbnail>
                    <CertificateTitle>{certificate.lecture_title}</CertificateTitle>
                  </CertificateCard>
                ))
              ) : (
                // 수료증이 없는 경우
                <div style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 0',
                  color: 'rgba(55, 56, 60, 0.61)',
                  fontSize: '16px',
                  gap: '8px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📜</div>
                  <div>아직 발급된 수료증이 없습니다.</div>
                  <div style={{ fontSize: '14px' }}>강의를 완료하면 수료증을 받을 수 있습니다.</div>
                </div>
              )}
            </CertificateGrid>
          </Section>

          {/* 나의 게시글 */}
          <Section>
            <SectionHeader>
              <SectionTitle>나의 게시글</SectionTitle>
              <SectionLink onClick={() => navigate('/mypage/activity')}>
                나의 활동
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SectionLink>
            </SectionHeader>
            <PostsContainer>
              {isLoadingMyPosts ? (
                // 로딩 상태
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100px',
                  width: '100%',
                  color: 'rgba(55, 56, 60, 0.61)',
                  fontSize: '14px'
                }}>
                  게시글 로딩 중...
                </div>
              ) : myPosts.length > 0 ? (
                // 실제 게시글 데이터
                <>
                  <PostDivider />
                  {myPosts.map((post) => (
                    <div key={post.post_id} style={{ width: '100%' }}>
                      <PostItem 
                        onClick={() => {
                          // 게시글 상세페이지로 이동
                          console.log('게시글 클릭:', post.post_id);
                          navigate(`/community/post/${post.post_id}`);
                        }}
                      >
                        <PostContent>
                          <PostTitle>{post?.title}</PostTitle>
                          <PostMeta>
                            <PostCategories>
                              <PostCategory>{post.post_category}</PostCategory>
                              {post.lecture_name && (
                                <>
                                  <MetaDivider />
                                  <PostCategory>{post.lecture_name}</PostCategory>
                                </>
                              )}
                              {post.chapter && (
                                <>
                                  <MetaDivider />
                                  <PostCategory>챕터 {post.chapter}</PostCategory>
                                </>
                              )}
                              {post.study_name && (
                                <>
                                  <MetaDivider />
                                  <PostCategory>{post.study_name}</PostCategory>
                                </>
                              )}
                            </PostCategories>
                            <PostStats>
                              <PostStat>
                                {new Date(post.created_at).toLocaleDateString('ko-KR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                }).replace(/\./g, '년').replace(/,/g, ' ')}
                              </PostStat>
                              <MetaDivider />
                              <PostStat>댓글 {post.reply_cnt}</PostStat>
                              <MetaDivider />
                              <PostStat>조회수 {post.view_cnt}</PostStat>
                            </PostStats>
                          </PostMeta>
                        </PostContent>
                        {post.pinned && (
                          <PinnedBadge>고정</PinnedBadge>
                        )}
                      </PostItem>
                      <PostDivider />
                    </div>
                  ))}
                </>
              ) : (
                // 게시글이 없는 경우
                <NoPostsContainer>
                  작성한 게시글이 없습니다.
                </NoPostsContainer>
              )}
            </PostsContainer>
          </Section>
        </MainContainer>
      </Container>
    </MyPageLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 48px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  color: black;
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30.01px;
  margin: 0;
`;

const SectionLink = styled.div`
  height: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: rgba(55, 56, 60, 0.8);
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const RecentLectureCard = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const LectureInfo = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;
`;

const LectureThumbnail = styled.div`
  width: 160px;
  height: 106.67px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LectureDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

const LectureTags = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
`;

const LectureTag = styled.span`
  height: 20px;
  padding-left: 6px;
  padding-right: 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
  word-wrap: break-word;
`;

const LectureContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
`;

const LectureTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const LectureInstructor = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

const LectureActions = styled.div`
  width: 120px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
`;

const ActionButton = styled.button<{ primary?: boolean; secondary?: boolean }>`
  width: 100%;
  padding: 9px 10px;
  border-radius: 10px;
  border: ${props => props.secondary ? '1px solid rgba(112, 115, 124, 0.16)' : 'none'};
  background: ${props => props.primary ? '#448181' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#448181'};
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PlayIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ProgressSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubSectionHeader = styled.div`
  height: 26px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 12px;
`;

const SubSectionTitle = styled.h3`
  color: black;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
  margin: 0;
`;

const ProgressCard = styled.div`
  width: 100%;
  padding: 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProgressTop = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ProgressLabel = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const DropdownButton = styled.button`
  height: 36px;
  padding: 7px 11px;
  border-radius: 10px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  color: black;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: 0.14px;
  cursor: pointer;
  min-width: 100px;
  
  &:hover {
    background: rgba(23, 23, 25, 0.04);
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  cursor: pointer;
  
  &:hover {
    background: rgba(112, 115, 124, 0.04);
  }
`;

const ProgressMain = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressValue = styled.div`
  color: #448181;
  font-size: 36px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 48.02px;
`;

const ProgressMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

const ProgressTime = styled.div`
  color: #448181;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const ProgressDescription = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(112, 115, 124, 0.08);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: #448181;
`;

const TwoColumnStats = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const StudyTimeSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StudyTimeCard = styled.div`
  width: 100%;
  padding: 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StudyTimeTop = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StudyTimeLabel = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const StudyTimeMain = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;
  margin: 24px 0;
`;

const StudyTimeValue = styled.div`
  color: #448181;
  font-size: 36px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 48.02px;
`;

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartLine = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const ChartBarContainer = styled.div`
  flex: 1;
  height: 194px;
  margin: 0 2px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  cursor: pointer;
`;

const ChartBar = styled.div<{ height: number; $isHovered?: boolean }>`
  width: 100%;
  height: ${props => props.height}px;
  background: #E2F0F0;
  border-radius: 8px 8px 0 0;
`;

const HoverOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.04;
  background: var(--Label-Normal, #171719);
`;

const Tooltip = styled.div`
  width: auto;
  height: auto;
  padding: 12px;
  background: var(--Static-White, white);
  border-radius: 8px;
  outline: 1px var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16)) solid;
  outline-offset: -1px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: inline-flex;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TooltipDate = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 13px;
  font-family: 'Pretendard JP';
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

const TooltipTime = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 13px;
  font-family: 'Pretendard JP';
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

const ChartBars = styled.div`
  width: 100%;
  height: 194px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  position: relative;
`;

const AttendanceSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AttendanceCard = styled.div`
  width: 100%;
  padding: 32px 14px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const AttendanceHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CalendarMonth = styled.div`
  color: #448181;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
  padding: 0 18px;
`;

const CalendarNavigation = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  padding-right: 18px;
`;

const CalendarArrow = styled.button`
  background: transparent;
  border: none;
  height: 21px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    opacity: 0.7;
  }
  
  img {
    display: block;
  }
  
  &:hover {
    opacity: 0.7;
  }
`;

const CalendarContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CalendarWeekdays = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 24px 0;
  align-items: center;
`;

const CalendarWeekday = styled.div`
  width: 100%;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const CalendarGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 24px 0;
  align-items: center;
`;

const CalendarDay = styled.div`
  width: 100%;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CalendarDate = styled.div<{ attendanceType?: string | null }>`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
  position: relative;
  z-index: 10; /* 텍스트가 배경 위에 오도록 */
  
  ${props => props.attendanceType === 'outline' && `
    &::before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 1px solid #C5E0E0;
      background: transparent;
      z-index: -1;
      box-sizing: border-box;
    }
  `}
  
  ${props => props.attendanceType === 'filled' && `
    &::before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #E2F0F0;
      border: none;
      z-index: -1;
      box-sizing: border-box;
    }
  `}
  
  ${props => props.attendanceType === 'border' && `
    &::before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #E2F0F0;
      border: 1px solid #C5E0E0;
      z-index: -1;
      box-sizing: border-box;
    }
  `}
`;

const LectureGrid = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const LectureCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
`;

const LectureCardThumbnail = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 3/2;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const LectureCardContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
`;

const LectureCardTags = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
`;

const LectureCardDetails = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
`;

const LectureCardTitle = styled.h4`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const LectureCardInstructor = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  margin: 0;
`;

const CertificateGrid = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const CertificateCard = styled.div`
  width: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
`;

const CertificateThumbnail = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 1/1;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CertificateTitle = styled.h4`
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.09px;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 0;
`;

const PostDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const PostItem = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const PostContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const PostTitle = styled.h4`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
  margin: 0;
`;

const PostMeta = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PostCategories = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const PostCategory = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const PostStat = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const MetaDivider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const PinnedBadge = styled.div`
  position: absolute;
  top: 32px;
  right: 0;
  padding: 4px 12px;
  background-color: #ff6b6b;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 16px;
  line-height: 1;
`;

const NoPostsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  line-height: 26.01px;
  word-wrap: break-word;
`;

// 스켈레톤 UI 컴포넌트들
const SkeletonThumbnail = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonTag = styled.div`
  width: 40px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonTitle = styled.div`
  width: 80%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonInstructor = styled.div`
  width: 60%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonButton = styled.div`
  width: 100%;
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 10px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 에러 UI 컴포넌트들
const ErrorCard = styled.div`
  width: 100%;
  padding: 32px;
  border-radius: 12px;
  border: 1px solid rgba(231, 76, 60, 0.2);
  background: rgba(231, 76, 60, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
`;

const ErrorSubMessage = styled.div`
  color: rgba(231, 76, 60, 0.7);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

// 강의 없음 UI 컴포넌트들
const NoLectureCard = styled.div`
  width: 100%;
  padding: 48px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: rgba(112, 115, 124, 0.02);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const NoLectureMessage = styled.div`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const NoLectureSubMessage = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin-bottom: 8px;
`;

// 드롭다운 관련 스타일 컴포넌트
const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  max-height: 400px;
  background: white;
  border-radius: 16px;
  border: 1px solid var(--Line-Solid-Neutral, #EAEBEC);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  overflow: hidden;
  margin-top: 8px;
`;

const DropdownScrollContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--Fill-Strong, rgba(112, 115, 124, 0.16));
    border-radius: 1000px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--Label-Normal, #171719);
    opacity: 0.05;
    border-radius: 1000px;
  }
`;

const DropdownItem = styled.div<{$isSelected: boolean}>`
  padding: 12px 20px;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  position: relative;
  
  ${props => props.$isSelected && `
    background: rgba(23, 23, 25, 0.04);
    border-radius: 12px;
    margin: 0 8px;
  `}
  
  &:hover {
    background: rgba(23, 23, 25, 0.04);
    border-radius: 12px;
    margin: 0 8px;
  }
`;

const NoLectureButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  border: 1px solid #448181;
  background: #448181;
  color: white;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  
  &:hover {
    background: #3a6f6f;
    border-color: #3a6f6f;
  }
`;

export default MyPageHome;